// One dictation at a time: the microphone, asked for on the first press; a
// Transcribe URL the backend signs at the AudioContext's own rate; the
// samples framed on this thread and sent down the socket. Final text goes to
// the caller as it arrives, into a box that stays editable; partial text is
// only shown.

import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { assistantDictationUrl } from './firebase';
import {
  audioFrame,
  pcm16,
  readFrame,
  TranscriptAssembler,
  MIN_SAMPLE_RATE,
  MAX_SAMPLE_RATE,
} from './transcribe';

const WORKLET_URL = `${process.env.PUBLIC_URL}/dictation-worklet.js`;

// How long a stopped session may take to hand back its last words
const SETTLE_MS = 10 * 1000;

const END_OF_AUDIO = new Uint8Array(0);

// Missing outside a secure context and in some in-app browsers
export const isDictationSupported = () =>
  !!navigator.mediaDevices?.getUserMedia &&
  !!window.AudioContext &&
  !!window.AudioWorkletNode;

const microphoneMessage = (error) => {
  const name = error?.name ?? '';
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return 'Microphone access is blocked. Allow it for this site in the browser settings';
  }
  if (name === 'NotFoundError') return 'No microphone was found';
  if (name === 'NotReadableError') {
    return 'The microphone is in use by another app';
  }
  return 'Failed to open the microphone';
};

// No rate is asked for: some browsers refuse a microphone into a context at
// another, so the context's own rate is signed into the URL. Only a rate
// Transcribe cannot take is overridden.
const openContext = () => {
  const context = new AudioContext();
  const rate = context.sampleRate;
  if (rate >= MIN_SAMPLE_RATE && rate <= MAX_SAMPLE_RATE) return context;
  context.close().catch(() => {});
  return new AudioContext({ sampleRate: MAX_SAMPLE_RATE });
};

// `onText` receives each final stretch of speech. Failures are toasts.
// phase: 'idle' | 'starting' | 'listening' | 'stopping'
export const useDictation = (onText) => {
  const [phase, setPhase] = useState('idle');
  const [partial, setPartial] = useState('');
  const sessionRef = useRef(null);
  // Set while the microphone is being asked for, before a session exists
  const openingRef = useRef(null);
  const deliverRef = useRef(onText);
  deliverRef.current = onText;

  // A no-op for a session already ended, so a late socket or timer event
  // cannot end the one that replaced it
  const end = useCallback((session, failure) => {
    if (!session || sessionRef.current !== session) return;
    sessionRef.current = null;
    session.timers.forEach(clearTimeout);
    session.stream.getTracks().forEach((track) => track.stop());
    if (session.node) session.node.port.onmessage = null;
    session.context?.close().catch(() => {});
    const { socket } = session;
    if (socket) {
      socket.onopen = null;
      socket.onmessage = null;
      socket.onclose = null;
      if (socket.readyState <= WebSocket.OPEN) socket.close();
    }
    // Words still only partial are kept, not lost
    const leftover = session.assembler.partial;
    if (leftover) deliverRef.current(leftover);
    setPartial('');
    setPhase('idle');
    if (failure) toast.error(failure);
  }, []);

  const stop = useCallback(() => {
    const session = sessionRef.current;
    if (!session) {
      if (!openingRef.current) return;
      openingRef.current.cancelled = true;
      openingRef.current = null;
      setPhase('idle');
      return;
    }
    if (session.ending) return;
    session.ending = true;
    session.stream.getTracks().forEach((track) => track.stop());
    // Nothing heard and nowhere to send it yet: nothing to wait for
    if (!session.socket && !session.pending.length) {
      end(session, null);
      return;
    }
    setPhase('stopping');
    // The service answers the empty frame with the last finals and closes the
    // socket itself; a socket still opening sends it on open
    if (session.socket?.readyState === WebSocket.OPEN) {
      session.socket.send(audioFrame(END_OF_AUDIO));
    }
    session.timers.push(setTimeout(() => end(session, null), SETTLE_MS));
  }, [end]);

  const connect = useCallback(
    (session, { url, maxSeconds }) => {
      const socket = new WebSocket(url);
      socket.binaryType = 'arraybuffer';
      session.socket = socket;

      socket.onopen = () => {
        session.pending.splice(0).forEach((frame) => socket.send(frame));
        if (session.ending) socket.send(audioFrame(END_OF_AUDIO));
      };
      socket.onmessage = (event) => {
        let message;
        try {
          message = readFrame(new Uint8Array(event.data));
        } catch (error) {
          console.error(error);
          end(session, 'Dictation stopped: ' + error.message);
          return;
        }
        if (message.kind === 'error') {
          console.error(`Transcribe ${message.type}: ${message.message}`);
          end(session, `Dictation stopped: ${message.message}`);
          return;
        }
        const finals = session.assembler.apply(message.results);
        finals.forEach((text) => deliverRef.current(text));
        setPartial(session.assembler.partial);
      };
      socket.onclose = (event) => {
        const isLost = !session.ending && event.code !== 1000;
        if (isLost) console.error('Dictation socket closed', event.code);
        end(
          session,
          isLost
            ? 'Dictation stopped: the connection to the transcription service was lost'
            : null,
        );
      };

      // Counted from the first sample: audio heard while the URL was being
      // signed is sent too and counts against the same limit
      const left = maxSeconds * 1000 - (Date.now() - session.startedAt);
      const limit = setTimeout(
        () => {
          if (session.ending) return;
          toast.info('Dictation stopped at its time limit');
          stop();
        },
        Math.max(0, left),
      );
      session.timers.push(limit);
    },
    [end, stop],
  );

  const start = useCallback(async () => {
    if (sessionRef.current || openingRef.current) return;
    setPhase('starting');
    const attempt = { cancelled: false };
    openingRef.current = attempt;

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });
    } catch (error) {
      if (attempt.cancelled) return;
      openingRef.current = null;
      console.error(error);
      setPhase('idle');
      toast.error(microphoneMessage(error));
      return;
    }
    // Stopped or left while the browser asked: let the microphone go
    if (attempt.cancelled) {
      stream.getTracks().forEach((track) => track.stop());
      return;
    }
    openingRef.current = null;

    const session = {
      stream,
      context: null,
      node: null,
      socket: null,
      assembler: new TranscriptAssembler(),
      // Frames captured before the socket opens, sent on open
      pending: [],
      startedAt: Date.now(),
      ending: false,
      timers: [],
    };
    sessionRef.current = session;

    try {
      const context = openContext();
      session.context = context;
      // Made after an await, so a browser may start it suspended. Not
      // awaited: it would wait for a gesture that may never come.
      context.resume().catch(() => {});

      await context.audioWorklet.addModule(WORKLET_URL);
      if (sessionRef.current !== session) return;
      const node = new AudioWorkletNode(context, 'pcm-capture');
      session.node = node;
      node.port.onmessage = (event) => {
        if (session.ending) return;
        const frame = audioFrame(pcm16(event.data));
        if (session.socket?.readyState === WebSocket.OPEN) {
          session.socket.send(frame);
        } else {
          session.pending.push(frame);
        }
      };
      context.createMediaStreamSource(stream).connect(node);
      // Connected so the graph pulls it; it writes nothing, so nothing plays
      node.connect(context.destination);
      session.startedAt = Date.now();
      setPhase('listening');

      const { data } = await assistantDictationUrl({
        sampleRate: context.sampleRate,
      });
      if (sessionRef.current !== session) return;
      connect(session, data);
    } catch (error) {
      console.error(error);
      end(session, error?.message ?? 'Failed to start dictation');
    }
  }, [connect, end]);

  // Leaving the screen ends the session and lets the microphone go
  const abandon = useCallback(() => {
    if (openingRef.current) openingRef.current.cancelled = true;
    openingRef.current = null;
    end(sessionRef.current, null);
  }, [end]);

  useEffect(() => abandon, [abandon]);

  return { phase, partial, start, stop };
};
